import { Page } from "../Components/page/Page";
import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { AddressAliasContextProvider, AddressCard } from "../Components/Address.js";
import { FileHashList, FlexRowPair, HashText } from "../Components/Common.js";
import { TreasuryClient, getActiveClient, getClient } from "../lib/client";
import { useProfiles } from "../lib/profiles.js";
import { DateTime } from "luxon";

function Transaction({ tx }) {
  function DateBox({ date, twistId }) {
    return <div className="flex flex-col min-w-fit px-1">
      {DateTime.fromISO(date).toFormat("yyyy-MM-dd HH:mm:ss")}
    </div>;
  }

  /*
      <div className="max-w-[12rem]">
      <HashDisplay h={twistId} fontSize={"12px"}/>
      </div>
      */


  function WithdrawalBox({ amount }) {
    return (<div className="py-1 px-1 min-w-fit max-w-full"> {Number(amount).toFixed(3)} </div>);
  }

  function DepositBox({ amount }) {
    return (<div className="py-1 px-1 min-w-fit max-w-full">{Number(amount).toFixed(3)}</div>);
  }

  function ToggleDetailButton({ showing, callback }) {
    return (<a className="font-sans cursor-pointer hover:underline" onClick={() => {
      return callback(showing);
    }} >{showing ? "Hide" : "Details..."}</a>);
  }

  function BalanceBox({ balance }) {
    // this info is not in the binder so I will not be able to implement it
    // or maybe I could just count tx amounts backwards from current balance...
    return (
      <div className="py-1 px-1 min-w-fit">{Number(balance).toFixed(3)}</div>
    )
  }

  function DetailsBox({ tx }) {
    return (
      <div className="px-4 py-2 pl-20 min-w-fit max-w-[30rem] font-sans">
        <FlexRowPair kv={["Entry Operator", tx.data.entryAuthor]} />
        <FlexRowPair kv={["Entry Type", tx.data.entryType]} />
        <FlexRowPair kv={["Recorded Operation", tx.data.entryOperation]} />
        <FlexRowPair kv={["Number of Transacted Notes", tx.data.entryFiles.length]} />
        <FlexRowPair kv={["List of Transacted Notes", <FileHashList hl={tx.data.entryFiles} />]} />
      </div>

    );
  }

  if (!tx) {
    return (<></>);
  }

  const { getActiveProfile } = useProfiles()

  let fromAddress, destinationAddress;
  const entry = tx?.data?.entry;
  const twinAddress = getActiveProfile()?.address;
  if (entry) {
    fromAddress = entry.fromAddress;
    if (!fromAddress) {
      fromAddress = twinAddress;
    }
    destinationAddress = entry.destinationAddress;
    if (!destinationAddress) {
      destinationAddress = twinAddress;
    }
  }

  let isSend;
  if (tx?.data?.entryOperation) {
    isSend = tx.data.entryOperation == "send";

  }

  const [isShowingDetail, setIsShowingDetail] = useState(false);
  return <>
    <tr
      className={`font-mono text-sm border hover:brightness-95 border-y-slate-600 0 min-w-fit`} >
      <td className="w-56">
        <DateBox date={tx.timestamp} twistId={tx.twistId} />
      </td>
      <td className="min-w-[8rem] text-center p-1">
        {isSend && <AddressCard to={destinationAddress} />}
      </td>
      <td className="min-w-[8rem] text-center p-1">
        {!isSend && <AddressCard from={fromAddress} />}
      </td>
      <td className="min-w-fit truncate text-right">
        {isSend && <WithdrawalBox amount={entry?.amount} />}
      </td>
      <td className="min-w-fit truncate text-right">
        {!isSend && <DepositBox amount={entry?.amount} />}
      </td>
      <td className="min-w-fit max-w-full text-right">
        <BalanceBox balance={entry?.$balance} />
      </td>
      <td className="details text-right pr-4 w-[5rem]">
        <ToggleDetailButton showing={isShowingDetail} callback={() => {
          return setIsShowingDetail(!isShowingDetail);
        }} />
      </td>
    </tr>
    <tr hidden={!isShowingDetail} className={`font-mono text-sm border hover:brightness-95 border-y-slate-600 0 min-w-fit border-t-slate-200 border-t-2 border-b-2`}>
      <td colSpan="7">
        <DetailsBox tx={tx} />
      </td>
    </tr>
  </>

}

function TransactionList({ transactions }) {
  return <AddressAliasContextProvider>
    {transactions.map(t => <Transaction key={t.twistId || Math.random().toString()} tx={t} />)}
  </AddressAliasContextProvider>;
}

export default function Transactions() {

  const { activeProfile, getActiveProfile, updateProfile } = useProfiles();
  const [searchParams, _] = useSearchParams();
  const [note, setNote] = useState(null);
  const [balances, setBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [displayedTxs, setDisplayedTxs] = useState([]);

  useEffect(function defaultNoteHook() {
    if (!balances[0]) return;
    if (!note) {
      return navigate(`/transactions?note=${balances[0].type}`);
    }
  }, [balances])

  useEffect(function getNoteFromQueryHook() {
    setNote(searchParams.get("note"))
  }, [searchParams]);


  useEffect(function setSelectedNoteHook() {
    updateProfile(activeProfile(), profile => ({
      ...profile,
      selectedToken: note
    }))
  }, [note]);

  useEffect(function filterDisplayedTransactions() {
    const dispTxs = transactions.filter(tx => tx.data.entry.type == note);
    const dispTxsB = dispTxs.reduceRight((arr, tx) => {
      tx.data.entry.$amount = tx.data.entryOperation == "send" ? -tx.data.entry.amount : tx.data.entry.amount;
      if (arr.length == 0) {
        tx.data.entry.$balance = tx.data.entry.$amount;
      } else {
        tx.data.entry.$balance = arr[0].data.entry.$balance + tx.data.entry.$amount;
      }
      arr.unshift(tx);
      return arr;
    }, []);

    setDisplayedTxs(dispTxsB);
  }, [note, transactions])

  const [twinClient, setTwinClient] = useState(getActiveClient())
  useEffect(function updateTwinClient() {
    setTwinClient(getClient(getActiveProfile()));
  }, [activeProfile()])

  const navigate = useNavigate();

  useEffect(function fetchBalancesHook() {
    (async function fetchBalances() {
      if (!twinClient) return;
      let allBalances = await twinClient.balance();
      setBalances(allBalances);
    })();
  }, [twinClient]);

  useEffect(function fetchTransactionsHook() {
    (async function fetchTransactions() {
      if (!twinClient) return;
      let binder = await twinClient.binder().catch(e => {
        console.warn("Your Internet sucks!")
        return JSON.parse(localStorage.getItem("binder"))
      });
      localStorage.setItem("binder", JSON.stringify(binder))
      let transactions = binder.filter(b => b?.data?.entryType == "dqTX");
      setTransactions(transactions);
    })();
  }, [twinClient]);

  const notes = balances.map(b => b.type);

  function toggleDropDown() {
    let dropdown = document.querySelector("#dropdownButton #dropdown");
    if (!dropdown) return
    dropdown.classList.toggle("hidden")
  }

  function noteDiv(h) {
    function doDropDownSelect(e) {
      e.preventDefault();
      let selected = e.target.title;
      if (selected) {
        if (selected != note) {
          navigate(`/transactions?note=${selected}`)
        } else {
          console.log("Note type it the same; not selecting")
        }
      }
      return toggleDropDown();
    }
    if (!h) return <></>;
    return (
      <div key={h}
        title={h}
        className={`cursor-pointer hover:bg-gray-200 p-4 font-mono ${h == note && "font-bold"}`}
        onClick={doDropDownSelect} >
        <HashText h={h} />
      </div>);
  }

  function txs2csv(txs) {
    function csvArr2str(csvArr) {
      return csvArr.map(r => r.join(",")).join("\n");
    }

    function csvEntry(tx) {
      // tx -> array of numbers / text for csv
      if (!tx) return [];

      let entry = tx?.data?.entry;
      let twinAddress = getActiveProfile()?.address;
      tx.fromAddress = entry.fromAddress || twinAddress;
      tx.toAddress = entry.destinationAddress || twinAddress;
      let amount = entry.amount;
      let isSend = tx.data.entryOperation == "send";
      tx.withdrawal = isSend ? -amount : undefined;
      tx.deposit = !isSend ? amount : undefined;
      tx.details = {
        author: tx.data.entryAuthor,
        type: tx.data.entryType,
        operation: tx.data.entryOperation,
        files: tx.data.entryFiles
      };

      return [
        tx.timestamp,
        tx.toAddress,
        tx.fromAddress,
        tx.withdrawal,
        tx.deposit,
        tx.details.author,
        tx.details.type,
        tx.details.operation,
        tx.details.files.join(" ")
      ];
    }

    if (!txs) return ""
    const csv = [];
    const header = ["Date", "To", "From", "Withdrawal", "Deposit", "Details.Author", "Details.Type", "Details.Operation", "Details.Files"];
    csv.push(header);
    for (const tx of txs) {
      csv.push(csvEntry(tx))
    }
    return csvArr2str(csv)
  }

  function textBlobUri(txt = "") {
    return URL.createObjectURL(new Blob([txt], { type: "text/plain" }));
  }
  // add Page here

  return <Page>
    <div className="min-w-fit">
      {/* {!twinClient && <Navigate to="/login" />} */}
      {/* <a href="/inventory"
        className="text-xl hover:underline ">
        View inventory
      </a> */}
      <div className="py-3"></div>
      <div className="flex flex-row w-full">
        {/* <div className="max-w-fit">
          <BalanceCard balance={balances.find(b => b.type == note)} />
        </div> */}
        {/* <div className="mx-10 text-xl ~min-w-fit min-w-[20rem] text-center">
          <Todaize/>
        </div> */}

      </div>
      <div className="flex flex-row py-4 px-1 border-b">
        {/* <div className="relative" id="dropdownButton"> */}
        <div className="w-1/2 relative" id="dropdownButton">Showing {displayedTxs.length} transactions of note type&nbsp;

          <span id="button"
            className="w-fit hover:underline bg-white py-2 rounded cursor-pointer select-none font-mono"
            onClick={toggleDropDown} >
            <HashText h={note} />
          </span>
          <div
            id="dropdown"
            className="border rounded border-gray-300 bg-white absolute top-[50px] w-[400px] select-none hidden z-50"
          >
            <div className="p-2"><b>Note Type Selection</b><br />Select note type from this twin's transaction history</div>
            <hr />
            {notes.map(h => noteDiv(h))}
          </div>
        </div>

        <div className="w-1/2 justify-end flex flex-row gap-3">

          <a id="btn-dlcvs"
            hidden={displayedTxs.length == 0}
            download="transactions.csv"
            href={textBlobUri(txs2csv(displayedTxs))}
            className="pl-8 ml-3 hover:underline bg-no-repeat bg-[url(/public/img/csv.svg)]" >
            Download CSV
          </a>

          <a id="btn-dlbinder"
            href={`${getActiveClient()?.url}/toda/${getActiveProfile()?.binderId}?apiKey=${getActiveClient()?.apiKey}`}
            className="pl-8 ml-3 hover:underline bg-[url(/public/img/binder.svg)] bg-no-repeat">
            Download Binder
          </a>
        </div>
      </div>
      <hr className="py-2"></hr>
      <table className="table-auto w-full">
        <thead>
          <tr className="table-row border-b-4 border-b-slate-800 sticky top-0 z-10 bg-white min-w-fit">
            <th className="w-56 text-left py-2 pt-4">Timestamp ({DateTime.now().toFormat("ZZZZ")})</th>
            <th className="min-w-[8rem] text-left p-1 pt-4">To</th>
            <th className="min-w-[8rem] text-left p-1 pt-4">From</th>
            <th className="min-w-fit truncate text-right p-1 pt-4">Withdrawal</th>
            <th className="min-w-fit truncate text-right p-1 pt-4">Deposit</th>
            <th className="min-w-fit text-right p-1 pt-4">Balance</th>
            <th className="text-right p-1 pt-4 w-[5rem]"></th>
          </tr>
        </thead>
        <TransactionList transactions={displayedTxs} />
      </table>
    </div>
  </Page>

}
